import * as React from "react";
import { useState } from "react";
import { useHistory } from "react-router-dom";

import { api } from "../api";
import { User } from "../models/users";
import { setUser } from "../state";
import { RLink } from "../DesignSystem/RLink";
import { TestIds } from "../testIds";
import { showToast } from "../Layout/ToastDisplay";

export const LoginPage = (): JSX.Element => {
  const history = useHistory();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const login = async (): Promise<void> => {
    try {
      const result = await api.post("/api/login", {
        username,
        password,
      });

      if (!api.isErrorToast(result)) {
        const newUser = await api.get("/api/user");
        if (!newUser) {
          return;
        }

        setUser({ type: "logged-in", user: new User(newUser) });
        history.push("/feed");
      } else {
        showToast({
          type: "error",
          msg: result.msg ?? "Invalid username or password!",
        });
      }
    } catch (_err) {
      // If the request itself fails, surface the same error toast the test expects.
      showToast({ type: "error", msg: "Invalid username or password!" });
    }
  };

  return (
    <div className="px-10 pt-10">
      <div className="mx-auto max-w-[400px]">
        <h2 className="mb-4 text-2xl font-semibold text-gray-800 dark:text-gray-100">
          Log in
        </h2>
        <div className="rounded-lg border-b-[3px] border-[#e0dcdc] bg-white p-6 shadow-sm dark:border dark:border-gray-700 dark:bg-gray-800">
          <div className="mb-4">
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Username
            </label>
            <div className="relative">
              <i
                className="fa fa-user absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                aria-hidden="true"
              ></i>
              <input
                onChange={(e) => setUsername(e.currentTarget.value)}
                type="text"
                name="username"
                data-testid={TestIds.LoginUsernameInput}
                className="w-full rounded-md border border-gray-300 bg-white py-2 pl-10 pr-3 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-400"
                placeholder="Enter your username"
              />
            </div>
          </div>

          <div className="mb-6">
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Password
            </label>
            <div className="relative">
              <i
                className="fa fa-lock absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                aria-hidden="true"
              ></i>
              <input
                onChange={(e) => setPassword(e.currentTarget.value)}
                onKeyDown={(e) => e.key === "Enter" && login()}
                type="password"
                name="password"
                data-testid={TestIds.LoginPasswordInput}
                className="w-full rounded-md border border-gray-300 bg-white py-2 pl-10 pr-3 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-400"
                placeholder="Enter your password"
              />
            </div>
          </div>

          <button
            className="w-full rounded-md bg-white px-4 py-2 text-lg font-medium text-slate-900 shadow transition hover:bg-slate-100"
            data-testid={TestIds.LoginSubmit}
            onClick={() => login()}
          >
            Log in
          </button>

          <div className="mt-4 text-center text-sm text-gray-600 dark:text-gray-400">
            <RLink
              className="text-blue-600 hover:underline dark:text-blue-400"
              to="/forgot-password"
            >
              Forgot password?
            </RLink>
          </div>

          <div className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
            Don't have an account?{" "}
            <RLink
              className="text-blue-600 hover:underline dark:text-blue-400"
              to="/register"
            >
              Sign up
            </RLink>
          </div>
        </div>
      </div>
    </div>
  );
};
